import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listMine, removeEntry, syncEntry, runSyncPass } from "../../lib/photoQueue";
import { API_BASE } from "../../lib/api";
import { Button } from "../../components/ui/button";

const STATUS_META = {
    pending: { color: "text-warning", icon: Loader2, label: "wartet" },
    uploading: { color: "text-info", icon: Loader2, label: "lädt" },
    synced: { color: "text-secondary", icon: CheckCircle2, label: "fertig" },
    failed: { color: "text-destructive", icon: AlertTriangle, label: "fehler" },
};

export default function FotoQueuePage() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        setItems(await listMine());
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 3000);
        return () => clearInterval(iv);
    }, []);

    const retryAll = async () => {
        setBusy(true);
        await runSyncPass(API_BASE);
        await refresh();
        setBusy(false);
        toast.success("Sync-Lauf beendet.");
    };

    const retryOne = async (rec) => {
        setBusy(true);
        try {
            await syncEntry(rec, API_BASE);
            toast.success("Upload ok.");
        } catch (e) {
            toast.error(`Fehler: ${e.message}`);
        }
        await refresh();
        setBusy(false);
    };

    // R11: hängengebliebene Uploads (App-Abbruch) manuell wieder anstossbar --
    // aber nur wenn wirklich stale, ein echter laufender Upload würde sonst
    // doppelt hochgeladen.
    const isStaleUploading = (rec) => {
        if (rec.status !== "uploading") return false;
        const last = rec.lastAttemptAt ? new Date(rec.lastAttemptAt).getTime() : 0;
        return !last || Date.now() - last > 2 * 60 * 1000;
    };

    const removeFailed = async (rec) => {
        await removeEntry(rec.id);
        await refresh();
    };

    return (
        <div className="flex flex-col gap-4 px-4 pb-24 pt-4" data-testid="foto-queue-page">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold">Foto-Queue</h1>
                <Button size="sm" onClick={retryAll} disabled={busy} data-testid="foto-queue-retry-all">
                    <RefreshCw className="mr-1 h-4 w-4" /> Jetzt syncen
                </Button>
            </div>

            {items.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                    Keine Fotos in der Warteschlange.
                </div>
            ) : (
                <ul className="flex flex-col gap-2" data-testid="foto-queue-list">
                    {items.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                        const Icon = meta.icon;
                        return (
                            <li
                                key={r.id}
                                className="flex items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2"
                                data-testid={`foto-queue-item-${r.id}`}
                            >
                                <Icon
                                    className={`h-5 w-5 ${meta.color} ${
                                        r.status === "uploading" || r.status === "pending" ? "animate-spin" : ""
                                    }`}
                                />
                                <div className="flex-1 text-xs">
                                    <div className="font-medium text-foreground">
                                        {r.entityType} · {r.purpose}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {meta.label} · {r.attempts || 0} Versuche{" "}
                                        {r.lastError ? `· ${r.lastError}` : ""}
                                    </div>
                                </div>
                                {(r.status === "failed" || isStaleUploading(r)) ? (
                                    <>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => retryOne(r)}
                                            disabled={busy}
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => removeFailed(r)}
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
